import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import './PublicCustomerApp.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

const CANONICAL_PUBLIC_ORIGIN = 'https://itraxx.se';
const PUBLIC_AUTOCOMPLETE_LIMIT = 6;

const getPublicOrigin = () => {
  if (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) {
    return window.location.origin;
  }

  return CANONICAL_PUBLIC_ORIGIN;
};

const buildPublicUrl = (pathname, params) => {
  const url = new URL(pathname, getPublicOrigin());
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
};

const normalizeLang = (lang) => {
  if (!lang) return null;
  return String(lang).split('-')[0].toLowerCase();
};

const LOGO_URL = `${process.env.PUBLIC_URL || ''}/itraxx_logo_transparent.png`;

const LOYALTY_PREVIEW = {
  badge: '10%',
  currentTier: 'Trogen kund',
  ridesThisMonth: 12,
  ridesToNextTier: 8,
  nextTier: 'VIP',
  currentDiscountPercent: 10,
  nextDiscountPercent: 15
};

const PUBLIC_PREFERENCE_OPTIONS = {
  vehicle: [
    { key: 'standard', icon: '🚕', labelKey: 'public_customer_app.preferences.options.vehicle.standard', canRequire: false },
    { key: 'electric', icon: '⚡', labelKey: 'public_customer_app.preferences.options.vehicle.electric', canRequire: false },
    { key: 'eco_friendly', icon: '🌱', labelKey: 'public_customer_app.preferences.options.vehicle.eco_friendly', canRequire: true },
    { key: 'large', icon: '🚐', labelKey: 'public_customer_app.preferences.options.vehicle.large', canRequire: false },
    { key: 'premium', icon: '✨', labelKey: 'public_customer_app.preferences.options.vehicle.premium', canRequire: false },
    { key: 'wheelchair', icon: '♿', labelKey: 'public_customer_app.preferences.options.vehicle.wheelchair', canRequire: true },
    { key: 'child_seat', icon: '👶', labelKey: 'public_customer_app.preferences.options.vehicle.child_seat', canRequire: false },
    { key: 'pet_friendly', icon: '🐾', labelKey: 'public_customer_app.preferences.options.vehicle.pet_friendly', canRequire: true },
    { key: 'extra_luggage', icon: '🧳', labelKey: 'public_customer_app.preferences.options.vehicle.extra_luggage', canRequire: false },
    { key: 'roof_rack', icon: '🎿', labelKey: 'public_customer_app.preferences.options.vehicle.roof_rack', canRequire: false },
    { key: 'bike_rack', icon: '🚲', labelKey: 'public_customer_app.preferences.options.vehicle.bike_rack', canRequire: false },
    { key: 'towbar', icon: '🪝', labelKey: 'public_customer_app.preferences.options.vehicle.towbar', canRequire: true },
    { key: 'four_wheel_drive', icon: '🛻', labelKey: 'public_customer_app.preferences.options.vehicle.four_wheel_drive', canRequire: true },
    { key: 'winter_tires', icon: '❄️', labelKey: 'public_customer_app.preferences.options.vehicle.winter_tires', canRequire: false },
    { key: 'door_to_door_help', icon: '🤝', labelKey: 'public_customer_app.preferences.options.vehicle.door_to_door_help', canRequire: false },
    { key: 'stroller_space', icon: '🍼', labelKey: 'public_customer_app.preferences.options.vehicle.stroller_space', canRequire: false },
    { key: 'taximeter', icon: '💵', labelKey: 'public_customer_app.preferences.options.vehicle.taximeter', canRequire: false }
  ],
  driver: [
    { key: 'quiet', icon: '🤫', labelKey: 'public_customer_app.preferences.options.driver.quiet', canRequire: false },
    { key: 'help_luggage', icon: '🧳', labelKey: 'public_customer_app.preferences.options.driver.help_luggage', canRequire: false },
    { key: 'english', icon: '🇬🇧', labelKey: 'public_customer_app.preferences.options.driver.english', canRequire: true },
    { key: 'female_driver', icon: '♀️', labelKey: 'public_customer_app.preferences.options.driver.female_driver', canRequire: false },
    { key: 'premium_service', icon: '🧤', labelKey: 'public_customer_app.preferences.options.driver.premium_service', canRequire: false },
    { key: 'pets_allowed', icon: '🐕', labelKey: 'public_customer_app.preferences.options.driver.pets_allowed', canRequire: false },
    { key: 'swedish', icon: '🇸🇪', labelKey: 'public_customer_app.preferences.options.driver.swedish', canRequire: false },
    { key: 'no_phone_calls', icon: '🔕', labelKey: 'public_customer_app.preferences.options.driver.no_phone_calls', canRequire: false },
    { key: 'text_only', icon: '💬', labelKey: 'public_customer_app.preferences.options.driver.text_only', canRequire: false },
    { key: 'assistance_needed', icon: '🦮', labelKey: 'public_customer_app.preferences.options.driver.assistance_needed', canRequire: false }
  ]
};

const buildAppError = (code) => {
  const error = new Error(code);
  error.code = code;
  return error;
};

const normalizeSuggestionLabel = (value) => String(value || '').trim();

const extractLocationHints = (label) => {
  const parts = String(label || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return parts.slice(1, 3);
  }

  if (parts.length >= 2) {
    return parts.slice(1);
  }

  return parts;
};

const dedupeAddressSuggestions = (entries) => {
  const seen = new Set();

  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const label = normalizeSuggestionLabel(entry?.label);
    if (!label) {
      return false;
    }

    const key = label.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const searchAddressCandidates = async (query, companyId, options = {}) => {
  const response = await axios.get(`${API_URL}/public/geo/search`, {
    params: {
      query,
      company_id: companyId || undefined,
      field: options.field || undefined,
      lat: Number.isFinite(Number(options.lat)) ? Number(options.lat) : undefined,
      lng: Number.isFinite(Number(options.lng)) ? Number(options.lng) : undefined,
      pickup_city: options.pickupCity || undefined
    }
  });

  return dedupeAddressSuggestions(response.data?.results || []).slice(0, PUBLIC_AUTOCOMPLETE_LIMIT);
};

const searchAddress = async (query, companyId, options = {}) => {
  const results = await searchAddressCandidates(query, companyId, options);
  const result = results[0];
  if (!result) {
    throw buildAppError('address_not_found');
  }

  return {
    lat: Number(result.lat),
    lng: Number(result.lng),
    label: result.label || query
  };
};

const reverseGeocode = async (lat, lng) => {
  const response = await axios.get(`${API_URL}/public/geo/reverse`, {
    params: { lat, lng }
  });

  const result = response.data?.result;
  if (!result) {
    throw buildAppError('reverse_failed');
  }

  return {
    lat: Number(result.lat),
    lng: Number(result.lng),
    label: result.label || `${lat}, ${lng}`
  };
};

const fetchPublicCompanyBootstrap = async (companyKey) => {
  const response = await axios.get(`${API_URL}/public/company-bootstrap`, {
    params: { company_key: companyKey }
  });

  return response.data || null;
};

const fetchRoute = async (pickup, dropoff) => {
  const response = await axios.get(`${API_URL}/public/geo/route`, {
    params: {
      pickup_lat: pickup.lat,
      pickup_lng: pickup.lng,
      dest_lat: dropoff.lat,
      dest_lng: dropoff.lng
    }
  });

  return {
    distanceKm: Number(response.data?.distance_km),
    durationMin: Number(response.data?.duration_min),
    geometry: Array.isArray(response.data?.geometry) ? response.data.geometry : []
  };
};

const calculateFallbackPrice = (distanceKm, durationMin) => {
  const distancePart = Math.max(0, Number(distanceKm || 0)) * 12;
  const durationPart = Math.max(0, Number(durationMin || 0)) * 2;
  return Math.round((45 + distancePart + durationPart) / 5) * 5;
};

const buildLoyaltyPreview = (basePrice) => {
  const savings = Number.isFinite(Number(basePrice))
    ? Math.round(Number(basePrice) * (LOYALTY_PREVIEW.currentDiscountPercent / 100))
    : 0;

  return {
    ...LOYALTY_PREVIEW,
    savings,
    progressPercent: Math.min(
      100,
      Math.round((LOYALTY_PREVIEW.ridesThisMonth / (LOYALTY_PREVIEW.ridesThisMonth + LOYALTY_PREVIEW.ridesToNextTier)) * 100)
    )
  };
};

const FitMapBounds = ({ points, routeGeometry }) => {
  const map = useMap();

  useEffect(() => {
    const validPoints = [
      ...points,
      ...(Array.isArray(routeGeometry) ? routeGeometry : [])
    ].filter((point) => Array.isArray(point) && point.length === 2);

    if (validPoints.length === 0) {
      return;
    }

    if (validPoints.length === 1) {
      map.setView(validPoints[0], 15);
      return;
    }

    map.fitBounds(validPoints, { padding: [32, 32] });
  }, [map, points, routeGeometry]);

  return null;
};

const PublicCustomerApp = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [pickupPoint, setPickupPoint] = useState(null);
  const [dropoffPoint, setDropoffPoint] = useState(null);
  const [locatingPickup, setLocatingPickup] = useState(false);
  const [mapFeedback, setMapFeedback] = useState('');
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [pickupSuggestionsLoading, setPickupSuggestionsLoading] = useState(false);
  const [dropoffSuggestionsLoading, setDropoffSuggestionsLoading] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [vehiclePreferences, setVehiclePreferences] = useState([]);
  const [driverPreferences, setDriverPreferences] = useState([]);
  const [requiredVehiclePreferences, setRequiredVehiclePreferences] = useState([]);
  const [requiredDriverPreferences, setRequiredDriverPreferences] = useState([]);

  const [homeWork, setHomeWork] = useState({ home: null, work: null });
  const [uploadedImages, setUploadedImages] = useState([]); // [{ id, name, size, url }]
  const [optionalSectionOpen, setOptionalSectionOpen] = useState(false);
  const [resolvedCompanyId, setResolvedCompanyId] = useState('');
  const [resolvedCompanyName, setResolvedCompanyName] = useState('');
  const MAX_IMAGES = 5;
  const dragDebounceRef = React.useRef(null); // NEW: Fix yxig kart-drag!

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedLang = normalizeLang(
    params.get('lang') || params.get('language') || params.get('locale') || params.get('lng')
  );
  const companyId = params.get('company_id') || params.get('company') || '';
  const companyKey = params.get('company_key') || params.get('companyKey') || '';
  const companyName = params.get('name') || '';
  const effectiveCompanyId = companyId || resolvedCompanyId;
  const effectiveCompanyName = companyName || resolvedCompanyName;
  const hasCompanyLink = Boolean(effectiveCompanyId);
  const activeLang = requestedLang || normalizeLang(i18n.resolvedLanguage || i18n.language) || 'en';
  const trackingUrl = useMemo(() => {
    const trimmed = String(trackingNumber || '').trim();
    return buildPublicUrl(trimmed ? `/track/${encodeURIComponent(trimmed)}` : '/track', { lang: activeLang });
  }, [activeLang, trackingNumber]);
  const defaultTrackingUrl = useMemo(() => buildPublicUrl('/track', { lang: activeLang }), [activeLang]);
  const loyaltyPreview = useMemo(() => buildLoyaltyPreview(trip?.estimatedPrice), [trip]);
  const pickupMarkerPosition = pickupPoint ? [pickupPoint.lat, pickupPoint.lng] : null;
  const dropoffMarkerPosition = dropoffPoint ? [dropoffPoint.lat, dropoffPoint.lng] : null;
  const mapPoints = [
    pickupMarkerPosition,
    dropoffMarkerPosition
  ].filter(Boolean);
  const pickupLocationHints = useMemo(
    () => extractLocationHints(pickupPoint?.label || pickupAddress),
    [pickupAddress, pickupPoint]
  );
  const selectedPreferenceCount = vehiclePreferences.length
    + driverPreferences.length
    + requiredVehiclePreferences.length
    + requiredDriverPreferences.length;
  const hasSavedPlaces = Boolean(homeWork.home || homeWork.work);

  useEffect(() => {
    const storageKey = 'itraxx_public_lang';
    const storedLang = normalizeLang(window.localStorage.getItem(storageKey));
    const nextLang = requestedLang || storedLang;
    const currentLang = normalizeLang(i18n.resolvedLanguage || i18n.language);

    if (!nextLang || nextLang === currentLang) {
      return;
    }

    window.localStorage.setItem(storageKey, nextLang);
    i18n.changeLanguage(nextLang).catch(() => {});
  }, [i18n, requestedLang]);

  useEffect(() => {
    document.documentElement.lang = (i18n.resolvedLanguage || 'en').split('-')[0];
    document.title = effectiveCompanyName ? String(effectiveCompanyName) : t('public_customer_app.hero.title');
  }, [effectiveCompanyName, i18n.resolvedLanguage, t]);

  useEffect(() => {
    if (companyId || !companyKey) {
      setResolvedCompanyId('');
      setResolvedCompanyName('');
      return;
    }

    let cancelled = false;

    fetchPublicCompanyBootstrap(companyKey)
      .then((bootstrap) => {
        if (cancelled) {
          return;
        }

        const nextCompanyId = bootstrap?.company_id;
        const nextCompanyName = bootstrap?.branding?.display_name || bootstrap?.branding?.legal_name || '';

        setResolvedCompanyId(nextCompanyId ? String(nextCompanyId) : '');
        setResolvedCompanyName(nextCompanyName ? String(nextCompanyName) : '');
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedCompanyId('');
          setResolvedCompanyName('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, companyKey]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('itraxx_homework_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setHomeWork({ home: parsed.home || null, work: parsed.work || null });
      }
    } catch {}
  }, []);

  const saveHomeWorkAddress = (kind, sourceField = 'pickup') => {
    const usePickupSource = sourceField === 'pickup';
    const label = usePickupSource ? pickupAddress : dropoffAddress;
    const point = usePickupSource ? pickupPoint : dropoffPoint;
    if (!label || !label.trim()) return;
    setHomeWork((prev) => {
      const next = { ...prev, [kind]: { label, point } };
      try { window.localStorage.setItem('itraxx_homework_v1', JSON.stringify(next)); } catch {}
      return next;
    });
    setMapFeedback(t(kind === 'home' ? 'public_customer_app.saved_home' : 'public_customer_app.saved_work') || (kind === 'home' ? 'Sparad som Hemma! ✓' : 'Sparad som Jobbet! ✓'));
  };

  const applyHomeWorkAddress = (kind, field) => {
    const addr = homeWork[kind];
    if (!addr) return;
    if (field === 'pickup') {
      setPickupAddress(addr.label);
      setPickupPoint(addr.point || null);
      setTrip(null);
    } else {
      setDropoffAddress(addr.label);
      setDropoffPoint(addr.point || null);
      setTrip(null);
    }
  };

  const imageFileRef = React.useRef(null);
  const handleImageFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setUploadedImages((prev) => {
      const remainingSlots = MAX_IMAGES - prev.length;
      if (remainingSlots <= 0 || !files.length) return prev;
      const toAdd = files.slice(0, remainingSlots).map((file, idx) => ({
        id: `${Date.now()}-${idx}-${file.name}`,
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file)
      }));
      return [...prev, ...toAdd];
    });
    if (event.target) event.target.value = '';
  };
  const removeUploadedImage = (id) => {
    setUploadedImages((prev) => {
      const next = prev.filter((img) => img.id !== id);
      const old = prev.find((img) => img.id === id);
      if (old?.url) try { URL.revokeObjectURL(old.url); } catch {}
      return next;
    });
  };

  const quickPrefs = [
    { key: 'eco_friendly', category: 'vehicle', icon: '🌱', labelKey: 'public_customer_app.preferences.options.vehicle.eco_friendly' },
    { key: 'pet_friendly', category: 'vehicle', icon: '🐕', labelKey: 'public_customer_app.preferences.options.driver.pets_allowed' },
    { key: 'wheelchair', category: 'vehicle', icon: '♿', labelKey: 'public_customer_app.preferences.options.vehicle.wheelchair' },
    { key: 'child_seat', category: 'vehicle', icon: '👶', labelKey: 'public_customer_app.preferences.options.vehicle.child_seat' },
    { key: 'quiet', category: 'driver', icon: '🤫', labelKey: 'public_customer_app.preferences.options.driver.quiet' },
    { key: 'english', category: 'driver', icon: '🇬🇧', labelKey: 'public_customer_app.preferences.options.driver.english' }
  ];

  const toggleSimplePref = (category, key) => {
    togglePreference(category, key);
    if (key === 'english') togglePreference('driver', 'swedish'); // mutal exclude
  };

  const togglePreference = (category, key) => {
    const setter = category === 'driver' ? setDriverPreferences : setVehiclePreferences;
    setter((current) => {
      const normalized = Array.isArray(current) ? current : [];
      if (normalized.includes(key)) {
        return normalized.filter((entry) => entry !== key);
      }
      return [...normalized, key];
    });
  };

  const toggleRequiredPreference = (category, key) => {
    const setter = category === 'driver' ? setRequiredDriverPreferences : setRequiredVehiclePreferences;
    setter((current) => {
      const normalized = Array.isArray(current) ? current : [];
      if (normalized.includes(key)) {
        return normalized.filter((entry) => entry !== key);
      }
      return [...normalized, key];
    });
  };

  const closePreferences = () => {
    setPreferencesOpen(false);
  };

  useEffect(() => {
    const trimmed = pickupAddress.trim();
    if (trimmed.length < 3 || pickupPoint?.label === trimmed) {
      setPickupSuggestions([]);
      setPickupSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    setPickupSuggestionsLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await searchAddressCandidates(trimmed, effectiveCompanyId, {
          field: 'pickup',
          lat: pickupPoint?.lat,
          lng: pickupPoint?.lng
        });

        if (!cancelled) {
          setPickupSuggestions(results);
        }
      } catch {
        if (!cancelled) {
          setPickupSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setPickupSuggestionsLoading(false);
        }
      }
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [effectiveCompanyId, pickupAddress, pickupPoint]);

  useEffect(() => {
    const trimmed = dropoffAddress.trim();
    if (trimmed.length < 3 || dropoffPoint?.label === trimmed) {
      setDropoffSuggestions([]);
      setDropoffSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    setDropoffSuggestionsLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await searchAddressCandidates(trimmed, effectiveCompanyId, {
          field: 'dropoff',
          lat: pickupPoint?.lat,
          lng: pickupPoint?.lng,
          pickupCity: pickupLocationHints[0] || undefined
        });

        if (!cancelled) {
          setDropoffSuggestions(results);
        }
      } catch {
        if (!cancelled) {
          setDropoffSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setDropoffSuggestionsLoading(false);
        }
      }
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [effectiveCompanyId, dropoffAddress, dropoffPoint, pickupLocationHints, pickupPoint]);

  const localizeError = (appError, fallbackKey = 'public_customer_app.errors.trip_failed') => {
    const code = appError?.code || appError?.message;
    const knownKeys = {
      address_not_found: 'public_customer_app.errors.address_not_found',
      reverse_failed: 'public_customer_app.errors.reverse_failed',
      route_failed: 'public_customer_app.errors.route_failed',
      trip_required_fields: 'public_customer_app.errors.trip_required_fields',
      gps_unsupported: 'public_customer_app.errors.gps_unsupported',
      gps_denied: 'public_customer_app.errors.gps_denied',
      gps_failed: 'public_customer_app.errors.gps_failed',
      pickup_update_failed: 'public_customer_app.errors.pickup_update_failed',
      booking_company_required: 'public_customer_app.errors.booking_company_required',
      booking_trip_required: 'public_customer_app.errors.booking_trip_required',
      booking_contact_required: 'public_customer_app.errors.booking_contact_required'
    };

    if (code && knownKeys[code]) {
      return t(knownKeys[code]);
    }

    return appError?.response?.data?.error || t(fallbackKey);
  };

  const estimateTrip = async ({ pickupCandidate = pickupPoint, dropoffCandidate = dropoffPoint } = {}) => {
    setLoading(true);
    setError('');
    setTrip(null);
    setBookingError('');
    setBookingSuccess(null);
    setMapFeedback('');

    try {
      if (!pickupAddress.trim() || !dropoffAddress.trim()) {
        throw buildAppError('trip_required_fields');
      }

      const [pickup, dropoff] = await Promise.all([
        pickupCandidate || searchAddress(pickupAddress.trim(), effectiveCompanyId, {
          field: 'pickup',
          lat: pickupPoint?.lat,
          lng: pickupPoint?.lng
        }),
        dropoffCandidate || searchAddress(dropoffAddress.trim(), effectiveCompanyId, {
          field: 'dropoff',
          lat: pickupPoint?.lat,
          lng: pickupPoint?.lng,
          pickupCity: pickupLocationHints[0] || undefined
        })
      ]);

      const route = await fetchRoute(pickup, dropoff);
      if (!Number.isFinite(route.distanceKm) || !Number.isFinite(route.durationMin)) {
        throw buildAppError('route_failed');
      }

      let estimatedPrice = null;
      let priceSource = 'fallback';

        if (effectiveCompanyId) {
        try {
          const pricingRes = await axios.post(`${API_URL}/calculate-price`, {
            pickup_lat: pickup.lat,
            pickup_lng: pickup.lng,
            dest_lat: dropoff.lat,
            dest_lng: dropoff.lng,
              company_id: Number(effectiveCompanyId),
              bolag_id: Number(effectiveCompanyId),
            pickup_time: pickupTime || undefined,
            pickup_tz_offset_minutes: -new Date().getTimezoneOffset()
          });
          if (pricingRes?.data?.totalt != null) {
            estimatedPrice = pricingRes.data.totalt;
            priceSource = 'company';
          }
        } catch {
          estimatedPrice = null;
        }
      }

      if (estimatedPrice == null) {
        estimatedPrice = calculateFallbackPrice(route.distanceKm, route.durationMin);
      }

      setPickupPoint(pickup);
      setDropoffPoint(dropoff);
      setPickupAddress(pickup.label);
      setDropoffAddress(dropoff.label);
      setTrip({
        pickup,
        dropoff,
        distanceKm: route.distanceKm,
        durationMin: route.durationMin,
        estimatedPrice,
        priceSource,
        routeGeometry: route.geometry
      });
    } catch (appError) {
      setError(localizeError(appError));
    } finally {
      setLoading(false);
    }
  };

  const handleEstimate = async (event) => {
    event.preventDefault();
    await estimateTrip();
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError(t('public_customer_app.errors.gps_unsupported'));
      return;
    }

    setLocatingPickup(true);
    setError('');
    setMapFeedback('');

    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const nextPickup = await reverseGeocode(position.coords.latitude, position.coords.longitude);
        setPickupPoint(nextPickup);
        setPickupAddress(nextPickup.label);
        setTrip(null);
        setMapFeedback(t('public_customer_app.feedback.pickup_current'));

        if (dropoffAddress.trim()) {
          await estimateTrip({ pickupCandidate: nextPickup, dropoffCandidate: dropoffPoint });
        }
      } catch (appError) {
        setError(localizeError(appError, 'public_customer_app.errors.reverse_failed'));
      } finally {
        setLocatingPickup(false);
      }
    }, (geoError) => {
      setLocatingPickup(false);
      if (geoError?.code === 1) {
        setError(t('public_customer_app.errors.gps_denied'));
        return;
      }
      setError(t('public_customer_app.errors.gps_failed'));
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };

  const handlePickupMarkerDragged = (event) => {
    const marker = event.target;
    const nextLatLng = marker.getLatLng();

    if (dragDebounceRef.current) window.clearTimeout(dragDebounceRef.current);
    setMapFeedback(t('public_customer_app.feedback.pickup_updating'));

    dragDebounceRef.current = window.setTimeout(async () => {
      try {
        const nextPickup = await reverseGeocode(nextLatLng.lat, nextLatLng.lng);
        setPickupPoint(nextPickup);
        setPickupAddress(nextPickup.label);

        if (dropoffPoint || dropoffAddress.trim()) {
          await estimateTrip({ pickupCandidate: nextPickup, dropoffCandidate: dropoffPoint });
        }

        setMapFeedback(t('public_customer_app.feedback.pickup_updated'));
      } catch (appError) {
        setError(localizeError(appError, 'public_customer_app.errors.pickup_update_failed'));
        setMapFeedback('');
      }
    }, 350);
  };

  const handleDropoffMarkerDragged = (event) => {
    const marker = event.target;
    const nextLatLng = marker.getLatLng();

    if (dragDebounceRef.current) window.clearTimeout(dragDebounceRef.current);
    setMapFeedback(t('public_customer_app.feedback.dropoff_updating'));

    dragDebounceRef.current = window.setTimeout(async () => {
      try {
        const nextDropoff = await reverseGeocode(nextLatLng.lat, nextLatLng.lng);
        setDropoffPoint(nextDropoff);
        setDropoffAddress(nextDropoff.label);

        if (pickupPoint || pickupAddress.trim()) {
          await estimateTrip({ pickupCandidate: pickupPoint, dropoffCandidate: nextDropoff });
        }

        setMapFeedback(t('public_customer_app.feedback.dropoff_updated') || 'Destination uppdaterad! ✓');
      } catch (appError) {
        setError(localizeError(appError, 'public_customer_app.errors.dropoff_update_failed') || 'Kunde inte uppdatera destination');
        setMapFeedback('');
      }
    }, 350);
  };

  const applyPickupSuggestion = (suggestion) => {
    if (!suggestion) {
      return;
    }

    setPickupPoint({
      lat: Number(suggestion.lat),
      lng: Number(suggestion.lng),
      label: suggestion.label
    });
    setPickupAddress(suggestion.label);
    setPickupSuggestions([]);
    setTrip(null);
    setMapFeedback('');
  };

  const applyDropoffSuggestion = (suggestion) => {
    if (!suggestion) {
      return;
    }

    setDropoffPoint({
      lat: Number(suggestion.lat),
      lng: Number(suggestion.lng),
      label: suggestion.label
    });
    setDropoffAddress(suggestion.label);
    setDropoffSuggestions([]);
    setTrip(null);
  };

  const buildPreferencesPayload = () => {
    const vehicle = Array.isArray(vehiclePreferences) ? vehiclePreferences : [];
    const driver = Array.isArray(driverPreferences) ? driverPreferences : [];
    const requiredVehicle = Array.isArray(requiredVehiclePreferences) ? requiredVehiclePreferences : [];
    const requiredDriver = Array.isArray(requiredDriverPreferences) ? requiredDriverPreferences : [];
    if (!vehicle.length && !driver.length && !requiredVehicle.length && !requiredDriver.length) {
      return null;
    }
    const requirements = { vehicle: {}, driver: {} };
    const preferences = {};
    const extras = {};

    if (requiredVehicle.includes('wheelchair')) {
      requirements.vehicle.wheelchair_accessible = true;
      extras.wheelchair = true;
    }
    if (requiredVehicle.includes('four_wheel_drive')) {
      requirements.vehicle.has_4x4 = true;
    }
    if (requiredVehicle.includes('towbar')) {
      requirements.vehicle.has_towbar = true;
    }
    if (requiredVehicle.includes('eco_friendly')) {
      requirements.vehicle.is_eco_friendly = true;
    }
    if (requiredVehicle.includes('pet_friendly')) {
      requirements.driver.pets_allowed = true;
      extras.animalTransport = true;
    }
    if (requiredDriver.includes('english')) {
      requirements.driver.speaks_english = true;
    }

    if (driver.includes('female_driver')) {
      preferences.driver_gender = 'female';
    }
    if (driver.includes('english')) {
      preferences.preferred_language = 'en';
    } else if (driver.includes('swedish')) {
      preferences.preferred_language = 'sv';
    }
    if (vehicle.includes('wheelchair')) {
      extras.wheelchair = true;
    }
    if (vehicle.includes('child_seat')) {
      extras.childSeat = true;
    }
    if (vehicle.includes('pet_friendly')) {
      extras.animalTransport = true;
    }
    if (vehicle.includes('extra_luggage')) {
      extras.luggage = true;
    }
    if (driver.includes('quiet')) {
      preferences.quiet_ride = true;
    }
    if (driver.includes('no_phone_calls') || driver.includes('text_only')) {
      preferences.contact_mode = driver.includes('text_only') ? 'text_only' : 'no_calls';
    }
    if (driver.includes('premium_service') || vehicle.includes('premium')) {
      preferences.preferred_service_level = 'premium';
    }
    if (driver.includes('pets_allowed')) {
      preferences.pet_friendly_preferred = true;
    }

    const wishedVehicle = vehicle.filter((option) => !requiredVehicle.includes(option));
    const wishedDriver = driver.filter((option) => !requiredDriver.includes(option));
    if (wishedVehicle.length) {
      preferences.vehicle_options = wishedVehicle;
    }
    if (wishedDriver.length) {
      preferences.driver_options = wishedDriver;
    }

    return {
      vehicle,
      driver,
      required_vehicle: requiredVehicle,
      required_driver: requiredDriver,
      requirements,
      preferences,
      extras
    };
  };

  const handleBookingSubmit = async () => {
    if (!effectiveCompanyId) {
      setBookingError(t('public_customer_app.errors.booking_company_required'));
      return;
    }
    if (!trip) {
      setBookingError(t('public_customer_app.errors.booking_trip_required'));
      return;
    }
    if (!customerName.trim() || !customerPhone.trim()) {
      setBookingError(t('public_customer_app.errors.booking_contact_required'));
      return;
    }

    setBookingLoading(true);
    setBookingError('');
    setBookingSuccess(null);

    try {
      const customerPreferences = buildPreferencesPayload();
      const response = await axios.post(`${API_URL}/public/customer-bookings`, {
        company_id: Number(effectiveCompanyId),
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_email: customerEmail.trim() || null,
        pickup_address: pickupAddress.trim(),
        pickup_lat: trip.pickup.lat,
        pickup_lng: trip.pickup.lng,
        dropoff_address: dropoffAddress.trim(),
        dropoff_lat: trip.dropoff.lat,
        dropoff_lng: trip.dropoff.lng,
        pickup_datetime: pickupTime || null,
        estimated_price: trip.estimatedPrice,
        order_details: {
          public_customer_app: true,
          price_source: trip.priceSource,
          distance_km: trip.distanceKm,
          duration_min: trip.durationMin,
          customer_preferences: customerPreferences || undefined,
          uploaded_images: uploadedImages.length > 0 ? uploadedImages.map((i) => ({ name: i.name, size: i.size, url: i.url })) : undefined,
          loyalty_preview: {
            tier: loyaltyPreview.currentTier,
            rides_this_month: loyaltyPreview.ridesThisMonth,
            next_tier: loyaltyPreview.nextTier,
            discount_percent: loyaltyPreview.currentDiscountPercent,
            preview_savings: loyaltyPreview.savings
          }
        },
        website: honeypot
      });
      const nextTrackingIdentifier = String(
        response.data?.public_tracking_identifier
        || response.data?.tracking_number
        || response.data?.orderNummer
        || response.data?.orderId
        || ''
      ).trim();

      setBookingSuccess({
        orderId: response.data?.orderId,
        orderNummer: response.data?.orderNummer,
        trackingNumber: nextTrackingIdentifier || null
      });
      if (nextTrackingIdentifier) {
        setTrackingNumber(nextTrackingIdentifier);
        window.location.assign(
          buildPublicUrl(`/track/${encodeURIComponent(nextTrackingIdentifier)}`, { lang: activeLang })
        );
      }
    } catch (appError) {
      setBookingError(localizeError(appError, 'public_customer_app.errors.booking_failed'));
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="public-customer-app">
      <div className="public-customer-shell">
        <div className="public-customer-top">
          <section className="brand-strip">
            <div className="brand-lockup">
              <div className="brand-logo">
                <img className="brand-logo-image" src={LOGO_URL} alt="iTRAXX" />
              </div>
              <div className="brand-text">
                <p className="eyebrow">{t('public_customer_app.hero.eyebrow')}</p>
                <strong>{effectiveCompanyName || 'iTRAXX'}</strong>
              </div>
            </div>
            <div className="context-row">
              <span className={`context-pill ${hasCompanyLink ? 'ok' : 'warn'}`}>
                {hasCompanyLink
                  ? t('public_customer_app.context.linked', { companyId: effectiveCompanyId })
                  : t('public_customer_app.context.missing')}
              </span>
              {effectiveCompanyName ? <span className="context-pill neutral">{effectiveCompanyName}</span> : null}
            </div>
          </section>

          <header className="public-customer-hero hybrid-hero">
            <div className="hero-copy">
              <h1>{t('public_customer_app.hero.title')}</h1>
              <p className="intro">{t('public_customer_app.hero.intro')}</p>
              <div className="action-row hero-actions">
                <a className="button-link" href="#booking-form">
                  {t('public_customer_app.trip.calculate')}
                </a>
                <a className="button-link secondary" href={trackingUrl}>
                  {t('public_customer_app.tracking.open')}
                </a>
              </div>
            </div>

            <div className="hero-stage">
              <div className="hero-stage-primary">
                <div className="hero-stage-header">
                  <span className="hero-stage-badge">
                    {hasCompanyLink
                      ? t('public_customer_app.hero.live_badge')
                      : t('public_customer_app.warning.title')}
                  </span>
                  <strong>{effectiveCompanyName || t('public_customer_app.trip.title')}</strong>
                </div>
                <div className="hero-stage-price">
                  <span className="summary-label">
                    {trip ? t('public_customer_app.trip.price') : t('public_customer_app.loyalty.eyebrow')}
                  </span>
                  <strong>
                    {trip
                      ? `${trip.estimatedPrice} kr`
                      : t('public_customer_app.loyalty.active_level', {
                        discountPercent: loyaltyPreview.currentDiscountPercent
                      })}
                  </strong>
                  <p className="muted">
                    {trip
                      ? (trip.priceSource === 'company'
                        ? t('public_customer_app.trip.price_source_company')
                        : t('public_customer_app.trip.price_source_fallback'))
                      : (hasCompanyLink ? t('public_customer_app.loyalty.potential_savings', { savings: loyaltyPreview.savings }) : t('public_customer_app.warning.body'))}
                  </p>
                </div>
              </div>

              <div className="hero-signal-grid">
                <div className="summary-card hero-signal-card">
                  <span className="summary-label">{t('public_customer_app.trip.distance')}</span>
                  <strong>{trip ? `${trip.distanceKm} km` : t('public_customer_app.trip.pickup_now')}</strong>
                  <p className="muted">
                    {trip ? trip.pickup.label : t('public_customer_app.trip.pickup_now_hint')}
                  </p>
                </div>
                <div className="summary-card hero-signal-card">
                  <span className="summary-label">{t('public_customer_app.trip.time')}</span>
                  <strong>{trip ? `${trip.durationMin} min` : t('public_customer_app.tracking.open_page')}</strong>
                  <p className="muted">
                    {trip ? trip.dropoff.label : t('public_customer_app.tracking.intro')}
                  </p>
                </div>
                <div className="summary-card hero-signal-card accent-loyalty">
                  <span className="summary-label">{t('public_customer_app.loyalty.title')}</span>
                  <strong>{loyaltyPreview.badge}</strong>
                  <p className="muted">
                    {t('public_customer_app.loyalty.rides_progress', {
                      ridesThisMonth: loyaltyPreview.ridesThisMonth,
                      ridesToNextTier: loyaltyPreview.ridesToNextTier,
                      nextTier: loyaltyPreview.nextTier,
                      nextDiscountPercent: loyaltyPreview.nextDiscountPercent
                    })}
                  </p>
                </div>
              </div>
            </div>
          </header>

          <section className="panel quick-action-strip">
            <div className="section-heading compact">
              <div>
                <p className="eyebrow">{t('public_customer_app.quick_actions.title')}</p>
                <h2>{t('public_customer_app.quick_actions.subtitle')}</h2>
              </div>
              <p className="muted">{t('public_customer_app.quick_actions.intro')}</p>
            </div>

            <div className="quick-action-grid">
              <button
                type="button"
                className="quick-action-card"
                onClick={handleUseCurrentLocation}
                disabled={locatingPickup}
              >
                <span className="quick-action-icon" aria-hidden="true">🎯</span>
                <span className="quick-action-copy">
                  <strong>{locatingPickup ? t('public_customer_app.trip.pickup_now_loading') : t('public_customer_app.trip.pickup_now')}</strong>
                  <span className="muted">{t('public_customer_app.trip.pickup_now_hint')}</span>
                </span>
              </button>

              <button
                type="button"
                className="quick-action-card"
                onClick={() => (homeWork.home ? applyHomeWorkAddress('home', 'pickup') : saveHomeWorkAddress('home', 'pickup'))}
                disabled={!homeWork.home && !pickupAddress.trim()}
              >
                <span className="quick-action-icon" aria-hidden="true">🏠</span>
                <span className="quick-action-copy">
                  <strong>{t('public_customer_app.trip.save_as_home')}</strong>
                  <span className="muted">{homeWork.home?.label || t('public_customer_app.quick_actions.home_hint')}</span>
                </span>
              </button>

              <button
                type="button"
                className="quick-action-card"
                onClick={() => (homeWork.work ? applyHomeWorkAddress('work', 'dropoff') : saveHomeWorkAddress('work', 'dropoff'))}
                disabled={!homeWork.work && !dropoffAddress.trim()}
              >
                <span className="quick-action-icon" aria-hidden="true">💼</span>
                <span className="quick-action-copy">
                  <strong>{t('public_customer_app.trip.save_as_work')}</strong>
                  <span className="muted">{homeWork.work?.label || t('public_customer_app.quick_actions.work_hint')}</span>
                </span>
              </button>

              <button
                type="button"
                className="quick-action-card"
                onClick={() => setPreferencesOpen(true)}
              >
                <span className="quick-action-icon" aria-hidden="true">⚙️</span>
                <span className="quick-action-copy">
                  <strong>{t('public_customer_app.preferences.open')}</strong>
                  <span className="muted">
                    {selectedPreferenceCount
                      ? t('public_customer_app.quick_actions.preferences_ready', { count: selectedPreferenceCount })
                      : t('public_customer_app.quick_actions.preferences_empty')}
                  </span>
                </span>
              </button>

              <a className="quick-action-card" href={trackingUrl}>
                <span className="quick-action-icon" aria-hidden="true">📍</span>
                <span className="quick-action-copy">
                  <strong>{t('public_customer_app.tracking.open')}</strong>
                  <span className="muted">{t('public_customer_app.tracking.intro')}</span>
                </span>
              </a>
            </div>
          </section>
        </div>

        <div className="experience-grid">
          <section className="panel trip-panel booking-panel" id="booking-form">
            <div className="booking-panel-header">
              <div className="section-heading">
                <div className="section-heading-row">
                  <div>
                    <p className="eyebrow">{t('public_customer_app.experience.eyebrow')}</p>
                    <h2>{t('public_customer_app.trip.title')}</h2>
                  </div>
                  <button
                    type="button"
                    className="preferences-trigger"
                    onClick={() => setPreferencesOpen(true)}
                    aria-label={t('public_customer_app.preferences.open')}
                    title={t('public_customer_app.preferences.open')}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                      <path
                        d="M10 3h4l1 3h3l2 4-2 4h-3l-1 3h-4l-1-3H6l-2-4 2-4h3l1-3Z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                    {selectedPreferenceCount ? (
                      <span className="preferences-badge" aria-hidden="true">
                        {selectedPreferenceCount}
                      </span>
                    ) : null}
                  </button>
                </div>
                <p className="muted">{t('public_customer_app.trip.intro')}</p>
              </div>

              <div className="booking-panel-metrics">
                <div className="metric-tile">
                  <span className="summary-label">{t('public_customer_app.experience.step_route')}</span>
                  <strong>{trip ? `${trip.distanceKm} km` : '1'}</strong>
                  <p className="muted">{t('public_customer_app.trip.pickup')} + {t('public_customer_app.trip.dropoff')}</p>
                </div>
                <div className="metric-tile">
                  <span className="summary-label">{t('public_customer_app.experience.step_options')}</span>
                  <strong>{selectedPreferenceCount}</strong>
                  <p className="muted">{t('public_customer_app.trip.more_options')}</p>
                </div>
                <div className="metric-tile">
                  <span className="summary-label">{t('public_customer_app.experience.step_contact')}</span>
                  <strong>{customerPhone.trim() ? t('common.ok') : '3'}</strong>
                  <p className="muted">{t('public_customer_app.trip.contact')}</p>
                </div>
              </div>
            </div>

            <div className="booking-workspace">
              <form className="trip-form" onSubmit={handleEstimate}>
              {hasSavedPlaces ? (
                <div className="saved-place-grid">
                  {homeWork.home ? (
                    <button
                      type="button"
                      className="saved-place-card"
                      onClick={() => applyHomeWorkAddress('home', 'pickup')}
                    >
                      <span className="saved-place-icon" aria-hidden="true">🏠</span>
                      <span className="saved-place-copy">
                        <strong>{t('public_customer_app.trip.save_as_home')}</strong>
                        <span className="muted small">{homeWork.home.label}</span>
                      </span>
                    </button>
                  ) : null}
                  {homeWork.work ? (
                    <button
                      type="button"
                      className="saved-place-card"
                      onClick={() => applyHomeWorkAddress('work', 'dropoff')}
                    >
                      <span className="saved-place-icon" aria-hidden="true">💼</span>
                      <span className="saved-place-copy">
                        <strong>{t('public_customer_app.trip.save_as_work')}</strong>
                        <span className="muted small">{homeWork.work.label}</span>
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : null}

                {/* NEW: SECTION 1 - PICKUP (UPPHÄMTNING) */}
                <div className="booking-section-label">
                  <span className="section-dot pickup"></span>
                  {t('public_customer_app.trip.pickup')}
                </div>

                {hasSavedPlaces ? (
                  <div className="address-chip-row">
                    <button
                      type="button"
                      disabled={!homeWork.home}
                      className={`address-chip home ${homeWork.home ? 'has-address' : ''}`}
                      onClick={() => applyHomeWorkAddress('home', 'pickup')}
                    >🏠 {t('public_customer_app.trip.save_as_home')}
                    </button>
                    <button
                      type="button"
                      disabled={!homeWork.work}
                      className={`address-chip work ${homeWork.work ? 'has-address' : ''}`}
                      onClick={() => applyHomeWorkAddress('work', 'pickup')}
                    >💼 {t('public_customer_app.trip.save_as_work')}
                    </button>
                  </div>
                ) : null}

                <label>
                  <input
                    type="text"
                    value={pickupAddress}
                    onChange={(event) => {
                      setPickupAddress(event.target.value);
                      setPickupPoint(null);
                      setTrip(null);
                      setMapFeedback('');
                    }}
                    placeholder={t('public_customer_app.trip.pickup_placeholder')}
                    required
                  />
                </label>

                {(pickupSuggestionsLoading || pickupSuggestions.length > 0) ? (
                  <div className="public-address-suggestion-block">
                    <div className="public-address-suggestion-heading">
                      <span>{t('public_customer_app.trip.suggested_addresses')}</span>
                      {pickupSuggestionsLoading ? (
                        <span className="public-address-suggestion-status">
                          {t('public_customer_app.trip.suggestions_loading')}
                        </span>
                      ) : (
                        <span className="public-address-suggestion-status accent-gps">
                          {t('public_customer_app.trip.suggestions_near_you')}
                        </span>
                      )}
                    </div>
                    {pickupSuggestions.length > 0 ? (
                      <div className="public-address-suggestion-list">
                        {pickupSuggestions.map((suggestion, index) => (
                          <button
                            key={`pickup-suggestion-${index}-${suggestion.label}`}
                            type="button"
                            className={`public-address-suggestion-button ${index === 0 ? 'accent-gps' : ''}`}
                            onClick={() => applyPickupSuggestion(suggestion)}
                          >
                            <span className="public-address-suggestion-label">{suggestion.label}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="pickup-actions-grid">
                  <button
                    type="button"
                    className="button-link button-gps-primary"
                    onClick={handleUseCurrentLocation}
                    disabled={locatingPickup}
                  >
                    <span aria-hidden>🎯</span>
                    {locatingPickup ? t('public_customer_app.trip.pickup_now_loading') : t('public_customer_app.trip.pickup_now')}
                  </button>
                  <button
                    type="button"
                    className="button-link button-pin-secondary"
                    title={t('public_customer_app.trip.map_title')}
                    onClick={() => {
                      if (pickupPoint || dropoffPoint) {
                        document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >🗺️</button>
                </div>
                <div className="save-actions-grid">
                  <button
                    type="button"
                    className="button-link subtle save-address-button"
                    onClick={() => saveHomeWorkAddress('home', 'pickup')}
                    disabled={!pickupAddress.trim()}
                  >
                    🏠 {t('public_customer_app.trip.save_as_home')}
                  </button>
                  <button
                    type="button"
                    className="button-link subtle save-address-button"
                    onClick={() => saveHomeWorkAddress('work', 'pickup')}
                    disabled={!pickupAddress.trim()}
                  >
                    💼 {t('public_customer_app.trip.save_as_work')}
                  </button>
                </div>

                <div className="booking-section-label">
                  <span className="section-dot dropoff"></span>
                  {t('public_customer_app.trip.dropoff')}
                </div>

                {hasSavedPlaces ? (
                  <div className="address-chip-row">
                    <button
                      type="button"
                      disabled={!homeWork.home}
                      className={`address-chip home ${homeWork.home ? 'has-address' : ''}`}
                      onClick={() => applyHomeWorkAddress('home', 'dropoff')}
                    >🏠 {t('public_customer_app.trip.save_as_home')}
                    </button>
                    <button
                      type="button"
                      disabled={!homeWork.work}
                      className={`address-chip work ${homeWork.work ? 'has-address' : ''}`}
                      onClick={() => applyHomeWorkAddress('work', 'dropoff')}
                    >💼 {t('public_customer_app.trip.save_as_work')}
                    </button>
                  </div>
                ) : null}

                <label>
                  <input
                    type="text"
                    value={dropoffAddress}
                    onChange={(event) => {
                      setDropoffAddress(event.target.value);
                      setDropoffPoint(null);
                      setTrip(null);
                    }}
                    placeholder={t('public_customer_app.trip.dropoff_placeholder')}
                    required
                  />
                </label>

                {(dropoffSuggestionsLoading || dropoffSuggestions.length > 0) ? (
                  <div className="public-address-suggestion-block">
                    <div className="public-address-suggestion-heading">
                      <span>{t('public_customer_app.trip.suggested_addresses')}</span>
                      {dropoffSuggestionsLoading ? (
                        <span className="public-address-suggestion-status">
                          {t('public_customer_app.trip.suggestions_loading')}
                        </span>
                      ) : (
                        <span className="public-address-suggestion-status accent-locality">
                          {pickupLocationHints[0]
                            ? t('public_customer_app.trip.suggestions_from_pickup', { locality: pickupLocationHints[0] })
                            : t('public_customer_app.trip.suggestions_nearby')}
                        </span>
                      )}
                    </div>
                    {dropoffSuggestions.length > 0 ? (
                      <div className="public-address-suggestion-list">
                        {dropoffSuggestions.map((suggestion, index) => (
                          <button
                            key={`dropoff-suggestion-${index}-${suggestion.label}`}
                            type="button"
                            className={`public-address-suggestion-button ${index === 0 ? 'accent-locality' : ''}`}
                            onClick={() => applyDropoffSuggestion(suggestion)}
                          >
                            <span className="public-address-suggestion-label">{suggestion.label}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="two-col-advanced">
                  <label className="col-item-time">
                    {t('public_customer_app.trip.pickup_time')}
                    <input
                      type="datetime-local"
                      value={pickupTime}
                      onChange={(event) => setPickupTime(event.target.value)}
                    />
                  </label>
                </div>

                <div className="save-actions-grid">
                  <button
                    type="button"
                    className="button-link subtle save-address-button"
                    onClick={() => saveHomeWorkAddress('home', 'dropoff')}
                    disabled={!dropoffAddress.trim()}
                  >
                    🏠 {t('public_customer_app.trip.save_as_home')}
                  </button>
                  <button
                    type="button"
                    className="button-link subtle save-address-button"
                    onClick={() => saveHomeWorkAddress('work', 'dropoff')}
                    disabled={!dropoffAddress.trim()}
                  >
                    💼 {t('public_customer_app.trip.save_as_work')}
                  </button>
                </div>

                <div className="optional-panel">
                  <button
                    type="button"
                    className={`optional-toggle ${optionalSectionOpen ? 'open' : ''}`}
                    onClick={() => setOptionalSectionOpen((current) => !current)}
                  >
                    <span>{t('public_customer_app.trip.more_options')}</span>
                    <span className="muted small">{t('public_customer_app.trip.more_options_intro')}</span>
                  </button>
                  {optionalSectionOpen ? (
                    <div className="optional-panel-body">
                      <div className="image-upload-section">
                        <div className="image-upload-header">
                          <h4>📸 {t('public_customer_app.trip.image_upload_title')} <span className="label-optional">(valfritt)</span></h4>
                          <p className="muted small">{t('public_customer_app.trip.image_upload_intro')}</p>
                        </div>
                        <div className="image-upload-body">
                          <button
                            type="button"
                            className="button-link button-upload"
                            onClick={() => imageFileRef.current?.click()}
                            disabled={uploadedImages.length >= MAX_IMAGES}
                          >
                            📷 {t('public_customer_app.trip.image_upload_button', { count: uploadedImages.length, max: MAX_IMAGES })}
                          </button>
                          <input
                            ref={imageFileRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleImageFileChange}
                          />
                        </div>
                        {uploadedImages.length > 0 ? (
                          <div className="uploaded-images-grid">
                            {uploadedImages.map((img) => (
                              <div key={img.id} className="uploaded-image-item">
                                <img src={img.url} alt={img.name} />
                                <button
                                  type="button"
                                  className="uploaded-image-remove"
                                  onClick={() => removeUploadedImage(img.id)}
                                >✕</button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>

                      <div className="simple-preferences-section">
                        <div className="image-upload-header">
                          <h4>⚙️ {t('public_customer_app.preferences.title')} <span className="label-optional">(valfritt)</span></h4>
                          <p className="muted small">{t('public_customer_app.trip.preference_intro')}</p>
                        </div>
                        <div className="simple-preferences-list">
                          {quickPrefs.map((pref) => (
                            <label key={pref.key} className="simple-pref-row">
                              <input
                                type="checkbox"
                                checked={(pref.category === 'vehicle' ? vehiclePreferences : driverPreferences).includes(pref.key)}
                                onChange={() => toggleSimplePref(pref.category, pref.key)}
                              />
                              <span className="simple-pref-icon" aria-hidden>{pref.icon}</span>
                              <span className="simple-pref-label">{t(pref.labelKey).replace(/\(.*\)/, '').trim() || pref.labelKey.split('.').pop()}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="booking-section-label">
                  <span className="section-dot contact"></span>
                  {t('public_customer_app.trip.contact')}
                </div>

                <div className="form-grid">
                  <label>
                    {t('public_customer_app.trip.name')}
                    <input
                      type="text"
                      value={customerName}
                      onChange={(event) => setCustomerName(event.target.value)}
                      placeholder={t('public_customer_app.trip.name_placeholder')}
                      required
                    />
                  </label>
                  <label>
                    {t('public_customer_app.trip.phone')}
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(event) => setCustomerPhone(event.target.value)}
                      placeholder={t('public_customer_app.trip.phone_placeholder')}
                      required
                    />
                  </label>
                </div>
                <label>
                  {t('public_customer_app.trip.email')}
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(event) => setCustomerEmail(event.target.value)}
                    placeholder={t('public_customer_app.trip.email_placeholder')}
                  />
                </label>
                <label className="hp-field" aria-hidden="true">
                  {t('public_customer_app.trip.website')}
                  <input
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    value={honeypot}
                    onChange={(event) => setHoneypot(event.target.value)}
                  />
                </label>

                <button type="submit" disabled={loading} className="button-calculate-full">
                  {loading ? (
                    <>
                      <span className="spinner-dot" />
                      <span className="spinner-dot" />
                      <span className="spinner-dot" />
                      {t('public_customer_app.trip.calculating')}
                    </>
                  ) : t('public_customer_app.trip.calculate')}
                </button>
              </form>

              <div className="live-rail">
                <div className="live-rail-header">
                  <p className="eyebrow">{t('public_customer_app.experience.live_title')}</p>
                  <h3>{t('public_customer_app.experience.live_subtitle')}</h3>
                  <p className="muted">{t('public_customer_app.experience.live_intro')}</p>
                </div>

                {error ? <div className="feedback error">{error}</div> : null}
                {mapFeedback ? <div className="feedback success">{mapFeedback}</div> : null}

                {(pickupMarkerPosition || dropoffMarkerPosition) ? (
                  <div className="map-shell live-panel">
                    <div className="map-header">
                      <h3>{t('public_customer_app.trip.map_title')}</h3>
                      <p className="muted">{t('public_customer_app.trip.map_intro')}</p>
                    </div>
                    <div className="map-frame">
                      <MapContainer center={pickupMarkerPosition || dropoffMarkerPosition} zoom={14} scrollWheelZoom={false}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitMapBounds points={mapPoints} routeGeometry={trip?.routeGeometry || []} />
                        {trip?.routeGeometry?.length ? (
                          <Polyline
                            positions={trip.routeGeometry}
                            pathOptions={{ color: '#24d38a', weight: 5, opacity: 0.9 }}
                          />
                        ) : null}
                        {pickupMarkerPosition ? (
                          <Marker
                            position={pickupMarkerPosition}
                            draggable
                            eventHandlers={{ dragend: handlePickupMarkerDragged }}
                          >
                            <Popup>{t('public_customer_app.trip.pickup_popup')}</Popup>
                          </Marker>
                        ) : null}
                        {dropoffMarkerPosition ? (
                          <Marker
                            position={dropoffMarkerPosition}
                            draggable
                            eventHandlers={{ dragend: handleDropoffMarkerDragged }}
                          >
                            <Popup>{t('public_customer_app.trip.dropoff_popup')}</Popup>
                          </Marker>
                        ) : null}
                      </MapContainer>
                    </div>
                  </div>
                ) : (
                  <div className="live-placeholder">
                    <span className="live-placeholder-icon" aria-hidden="true">🛰️</span>
                    <strong>{t('public_customer_app.experience.live_empty_title')}</strong>
                    <p className="muted">{t('public_customer_app.experience.live_empty_body')}</p>
                  </div>
                )}

                {trip ? (
                  <div className="trip-result live-panel">
                    <div className="trip-stats">
                      <div>
                        <span className="label">{t('public_customer_app.trip.distance')}</span>
                        <strong>{trip.distanceKm} km</strong>
                      </div>
                      <div>
                        <span className="label">{t('public_customer_app.trip.time')}</span>
                        <strong>{trip.durationMin} min</strong>
                      </div>
                      <div>
                        <span className="label">{t('public_customer_app.trip.price')}</span>
                        <strong>{trip.estimatedPrice} kr</strong>
                      </div>
                    </div>

                    <div className="price-preview-card">
                      <span className="price-preview-label">{t('public_customer_app.loyalty.eyebrow')}</span>
                      <strong>{t('public_customer_app.loyalty.active_level', { discountPercent: loyaltyPreview.currentDiscountPercent })}</strong>
                      <p className="muted">{t('public_customer_app.loyalty.potential_savings', { savings: loyaltyPreview.savings })}</p>
                    </div>

                    <p className="muted">
                      {trip.priceSource === 'company'
                        ? t('public_customer_app.trip.price_source_company')
                        : t('public_customer_app.trip.price_source_fallback')}
                    </p>

                    <p className="muted">
                      {t('public_customer_app.trip.from')}: {trip.pickup.label}
                      <br />
                      {t('public_customer_app.trip.to')}: {trip.dropoff.label}
                    </p>

                    <div className="action-row action-row-center">
                      <button
                        type="button"
                        className="button-link button-book-final"
                        onClick={handleBookingSubmit}
                        disabled={bookingLoading || !hasCompanyLink}
                      >
                        {bookingLoading ? (
                          <>
                            <span className="spinner-dot" />
                            <span className="spinner-dot" />
                            <span className="spinner-dot" />
                            {t('public_customer_app.trip.submitting')}
                          </>
                        ) : t('public_customer_app.trip.submit')}
                      </button>
                    </div>

                    {bookingError ? <div className="feedback error">{bookingError}</div> : null}
                    {bookingSuccess ? (
                      <div className="feedback success">
                        {t('public_customer_app.trip.submitted')}
                        {bookingSuccess.orderNummer ? ` ${t('public_customer_app.trip.submitted_reference', { reference: bookingSuccess.orderNummer })}` : ''}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {!hasCompanyLink ? (
                  <section className="panel warning inline-warning">
                    <h2>{t('public_customer_app.warning.title')}</h2>
                    <p>{t('public_customer_app.warning.body')}</p>
                    <p>{t('public_customer_app.warning.example')}</p>
                  </section>
                ) : null}
              </div>
            </div>
          </section>

          <div className="side-column">
            <section className="panel loyalty-panel">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">{t('public_customer_app.loyalty.eyebrow')}</p>
                  <h2>{t('public_customer_app.loyalty.title')}</h2>
                </div>
                <span className="loyalty-badge">{loyaltyPreview.badge}</span>
              </div>
              <div className="loyalty-progress">
                <div className="loyalty-progress-bar">
                  <div
                    className="loyalty-progress-fill"
                    style={{ width: `${loyaltyPreview.progressPercent}%` }}
                  />
                </div>
                <p className="muted">
                  <strong>
                    {t('public_customer_app.loyalty.rides_progress', {
                      ridesThisMonth: loyaltyPreview.ridesThisMonth,
                      ridesToNextTier: loyaltyPreview.ridesToNextTier,
                      nextTier: loyaltyPreview.nextTier,
                      nextDiscountPercent: loyaltyPreview.nextDiscountPercent
                    })}
                  </strong>
                </p>
                {trip ? (
                  <p className="loyalty-savings">
                    {t('public_customer_app.loyalty.potential_savings', { savings: loyaltyPreview.savings })}
                  </p>
                ) : null}
              </div>
            </section>

            <section className="panel perks-panel">
              <div className="section-heading compact">
                <div>
                  <p className="eyebrow">{t('public_customer_app.experience.why_eyebrow')}</p>
                  <h2>{t('public_customer_app.experience.why_title')}</h2>
                </div>
              </div>

              <ul className="status-list">
                <li>{t('public_customer_app.experience.why_item_loyalty')}</li>
                <li>{t('public_customer_app.experience.why_item_tracking')}</li>
                <li>{t('public_customer_app.experience.why_item_preferences')}</li>
                <li>{t('public_customer_app.experience.why_item_pickup')}</li>
              </ul>
            </section>

            <section className="panel">
              <div className="section-heading compact">
                <div>
                  <h2>{t('public_customer_app.tracking.title')}</h2>
                  <p className="muted">{t('public_customer_app.tracking.intro')}</p>
                </div>
              </div>

              <label>
                {t('public_customer_app.tracking.number')}
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(event) => setTrackingNumber(event.target.value)}
                  placeholder={t('public_customer_app.tracking.number_placeholder')}
                />
              </label>

              <div className="action-row">
                <a className="button-link secondary" href={trackingUrl}>
                  {t('public_customer_app.tracking.open')}
                </a>
                <a className="button-link subtle" href={defaultTrackingUrl}>
                  {t('public_customer_app.tracking.open_page')}
                </a>
              </div>
            </section>

            <section className="panel">
              <div className="section-heading compact">
                <div>
                  <h2>{t('public_customer_app.status.title')}</h2>
                </div>
              </div>

              <ul className="status-list">
                <li>{t('public_customer_app.status.item_route')}</li>
                <li>{t('public_customer_app.status.item_company')}</li>
                <li>{t('public_customer_app.status.item_qr')}</li>
                <li>{t('public_customer_app.status.item_pending')}</li>
                <li>{t('public_customer_app.status.item_pickup')}</li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      {preferencesOpen ? (
        <div
          className="public-preferences-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('public_customer_app.preferences.title')}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closePreferences();
            }
          }}
        >
          <div className="public-preferences-panel">
            <div className="public-preferences-header">
              <div className="public-preferences-title">
                <span className="public-preferences-title-icon" aria-hidden="true">⚙️</span>
                <strong>{t('public_customer_app.preferences.title')}</strong>
              </div>
              <button
                type="button"
                className="public-preferences-close"
                onClick={closePreferences}
                aria-label={t('common.close')}
                title={t('common.close')}
              >
                ✕
              </button>
            </div>

            <div className="public-preferences-section">
              <div className="public-preferences-section-title">
                <span aria-hidden="true">🚗</span>
                <span>{t('public_customer_app.preferences.vehicle')}</span>
              </div>
              <div className="public-preferences-grid">
                {PUBLIC_PREFERENCE_OPTIONS.vehicle.map((option) => {
                  const selected = vehiclePreferences.includes(option.key);
                  const required = requiredVehiclePreferences.includes(option.key);
                  return (
                    <div key={option.key} className="public-preference-stack">
                      <button
                        type="button"
                        className={`public-preference-tile ${selected ? 'selected' : ''} ${required ? 'required' : ''}`}
                        onClick={() => togglePreference('vehicle', option.key)}
                        title={t(option.labelKey)}
                        aria-label={t(option.labelKey)}
                      >
                        <span className="public-preference-icon" aria-hidden="true">{option.icon}</span>
                      </button>
                      {option.canRequire ? (
                        <button
                          type="button"
                          className={`public-preference-require ${required ? 'active' : ''}`}
                          onClick={() => toggleRequiredPreference('vehicle', option.key)}
                          title={t('public_customer_app.preferences.require')}
                          aria-label={t('public_customer_app.preferences.require')}
                        >
                          !
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="public-preferences-section">
              <div className="public-preferences-section-title">
                <span aria-hidden="true">🧑‍✈️</span>
                <span>{t('public_customer_app.preferences.driver')}</span>
              </div>
              <div className="public-preferences-grid">
                {PUBLIC_PREFERENCE_OPTIONS.driver.map((option) => {
                  const selected = driverPreferences.includes(option.key);
                  const required = requiredDriverPreferences.includes(option.key);
                  return (
                    <div key={option.key} className="public-preference-stack">
                      <button
                        type="button"
                        className={`public-preference-tile ${selected ? 'selected' : ''} ${required ? 'required' : ''}`}
                        onClick={() => togglePreference('driver', option.key)}
                        title={t(option.labelKey)}
                        aria-label={t(option.labelKey)}
                      >
                        <span className="public-preference-icon" aria-hidden="true">{option.icon}</span>
                      </button>
                      {option.canRequire ? (
                        <button
                          type="button"
                          className={`public-preference-require ${required ? 'active' : ''}`}
                          onClick={() => toggleRequiredPreference('driver', option.key)}
                          title={t('public_customer_app.preferences.require')}
                          aria-label={t('public_customer_app.preferences.require')}
                        >
                          !
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="public-preferences-footer">
              <button type="button" className="button-link" onClick={closePreferences}>
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PublicCustomerApp;
